import { ReactNode } from 'react';

import { ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { TypeChip } from '@backrs_ui/common-ui/src/molecules/Nugget/TypeChip';

import { Box, Theme, SxProps, Typography } from '@mui/material';

type PageHeaderProps = Readonly<{
  title: string;
  subtitle?: string;
  rightComponents?: ReactNode[];
  tag?: string;
  tagId?: string;
  onTagClick?: () => void;
  maxWidth?: number | null;
  profileImageUrl?: string;
  borderColor?: ColorToken;
  padding?: number;
  sxOverrides?: Partial<{
    container: SxProps<Theme>;
    titleContainer: SxProps<Theme>;
    rightContainer: SxProps<Theme>;
  }>;
  showBorder?: boolean;
  isSubheader?: boolean;
  titleAdornment?: ReactNode;
}>;

export const PageHeader = ({
  title,
  subtitle,
  rightComponents,
  tag,
  tagId,
  onTagClick,
  maxWidth = 1240,
  profileImageUrl,
  padding = 0,
  borderColor = 'Gray/700',
  sxOverrides,
  showBorder = true,
  isSubheader = false,
  titleAdornment,
}: PageHeaderProps) => {
  const subtitleVariant = isSubheader ? 'text-sm-regular' : 'text-md-regular';

  return (
    <Box
      sx={
        [
          containerSx(maxWidth, borderColor, padding, showBorder),
          sxOverrides?.container,
        ] as SxProps<Theme>
      }
    >
      {profileImageUrl && (
        <Box
          component="img"
          src={profileImageUrl}
          alt="Profile Image"
          sx={profileImageContainerSx}
        />
      )}
      <Box sx={[titleContainerSx, sxOverrides?.titleContainer] as SxProps<Theme>}>
        <Box sx={titleAndTagContainerSx}>
          <Typography variant="display-xs-semibold" sx={titleSx}>
            {title}
          </Typography>
          {tag && <TypeChip label={tag} id={tagId} onClick={onTagClick} />}
          {titleAdornment}
        </Box>
        {subtitle && (
          <Typography variant={subtitleVariant} sx={isSubheader ? subheaderSubtitleSx : subtitleSx}>
            {subtitle}
          </Typography>
        )}
      </Box>

      <Box sx={[rightComponentsContainerSx, sxOverrides?.rightContainer] as SxProps<Theme>}>
        {rightComponents?.map((component, index) => (
          <Box key={index} sx={{ minWidth: 0 }}>
            {component}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const containerSx =
  (
    maxWidth: number | null,
    borderColor: ColorToken,
    padding: number,
    showBorder: boolean,
  ): SxProps<Theme> =>
  ({ backrs }) => ({
    containerType: 'inline-size',
    display: 'flex',
    width: '100%',
    maxWidth: maxWidth ? `${maxWidth + padding * 2}px` : '100%',
    flexDirection: 'column',
    '@container (min-width: 600px)': {
      flexDirection: 'row',
      alignItems: 'flex-end',
      pb: backrs.spacing['spacing-3xl'],
    },
    flexWrap: 'nowrap',
    gap: backrs.spacing['spacing-2xl'],
    justifyContent: 'space-between',

    alignSelf: 'center',
    justifySelf: 'center',
    borderBottom: showBorder ? `1px solid ${backrs.colors[borderColor]}` : 'none',

    pb: backrs.spacing['spacing-2xl'],
    px: 0,
    '@container (min-width: 900px)': {
      px: `${padding}px`,
    },
  });

const profileImageContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '60px',
  height: '60px',
  borderRadius: backrs.radius['radius-full'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  alignSelf: 'flex-start',
  justifySelf: 'flex-start',
});

const titleContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: '50%',
};

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  alignSelf: 'flex-start',
  justifySelf: 'flex-start',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
});

const subheaderSubtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
  alignSelf: 'flex-start',
  justifySelf: 'flex-start',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
});

const rightComponentsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  '@container (min-width: 600px)': {
    justifyContent: 'flex-end',
  },
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-lg'],
  minWidth: 0,
  flexShrink: 1,
});

const titleAndTagContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});
